'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetThesesRow, useActiveWallet, useAuth, useGetThesesInfinite, useGetThesisReplyEligibility } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useOpenMarketMobileTradeForm } from '@/components/market/floating-trade-button';
import { MarketThesisPromptBar } from '@/components/market/market-thesis-prompt-bar';
import { ThesisFeed } from '@/components/social/debate/thesis-feed';
import type { ThesisReplyWriteAccess, ThesisReplyWriteStatus } from '@/components/social/debate/thesis-reply-write-access';
import type { ThesisItem } from '@/components/social/types';
import { FireIconSolid, Plant04IconSolid } from '@/components/ui/icon/glyphs';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { logger } from '@/lib/telemetry/logger';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MarketDebateSectionProps {
  marketPubkey: string;
  tradesFrom: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MarketDebateSection(t0) {
  const $ = _c(74);
  const {
    marketPubkey,
    tradesFrom
  } = t0;
  const t = useTranslations("social");
  const [sort, setSort] = useState("recent");
  const {
    isAuthenticated
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const openTradeForm = useOpenMarketMobileTradeForm();
  const userId = activeWallet?.address ?? null;
  const replyEligibilityEnabled = isAuthenticated;
  let t1;
  if ($[0] !== marketPubkey) {
    t1 = {
      marketPubkey
    };
    $[0] = marketPubkey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== replyEligibilityEnabled) {
    t2 = {
      enabled: replyEligibilityEnabled,
      retry: false
    };
    $[2] = replyEligibilityEnabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const replyEligibilityQuery = useGetThesisReplyEligibility(t1, t2);
  const replyWriteStatus = !isAuthenticated ? "signed-out" : replyEligibilityQuery.isPending ? "checking" : replyEligibilityQuery.isError ? "error" : replyEligibilityQuery.data?.allowed === true ? "eligible" : "ineligible";
  let t3;
  if ($[4] !== replyEligibilityQuery) {
    t3 = () => {
      replyEligibilityQuery.refetch();
    };
    $[4] = replyEligibilityQuery;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== replyEligibilityQuery.data || $[7] !== replyEligibilityQuery.dataUpdatedAt || $[8] !== replyWriteStatus || $[9] !== t3) {
    t4 = {
      status: replyWriteStatus,
      eligibility: replyEligibilityQuery.data,
      dataUpdatedAt: replyEligibilityQuery.dataUpdatedAt,
      refresh: t3
    };
    $[6] = replyEligibilityQuery.data;
    $[7] = replyEligibilityQuery.dataUpdatedAt;
    $[8] = replyWriteStatus;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const replyWriteAccess = t4;
  let t5;
  let t6;
  if ($[11] !== marketPubkey || $[12] !== replyEligibilityEnabled || $[13] !== replyEligibilityQuery.isError) {
    t5 = () => {
      if (!replyEligibilityEnabled || !replyEligibilityQuery.isError) {
        return;
      }
      logger.warn("[MarketDebateSection] reply access check failed", {
        marketPubkey
      });
    };
    t6 = [marketPubkey, replyEligibilityEnabled, replyEligibilityQuery.isError];
    $[11] = marketPubkey;
    $[12] = replyEligibilityEnabled;
    $[13] = replyEligibilityQuery.isError;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== marketPubkey || $[17] !== sort) {
    t7 = {
      marketPubkey,
      sort
    };
    $[16] = marketPubkey;
    $[17] = sort;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      resetPaginationOnRefetch: true
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const thesesQuery = useGetThesesInfinite(t7, t8);
  let t10;
  let t9;
  if ($[20] !== marketPubkey || $[21] !== sort || $[22] !== thesesQuery.isError) {
    t9 = () => {
      if (!thesesQuery.isError) {
        return;
      }
      logger.warn("market.debate.fetch_failed", {
        marketPubkey,
        sort
      });
    };
    t10 = [marketPubkey, sort, thesesQuery.isError];
    $[20] = marketPubkey;
    $[21] = sort;
    $[22] = thesesQuery.isError;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  const {
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = thesesQuery;
  const rows = useLiveListData(thesesQuery.queryKey);
  const pageCount = rows.length > 0 ? 1 : 0;
  let t11;
  if ($[25] !== rows) {
    t11 = rows.map(_temp);
    $[25] = rows;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const theses = t11;
  let t12;
  if ($[27] !== t) {
    t12 = t("debate_newest");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = {
      key: "recent",
      label: t12,
      icon: Plant04IconSolid
    };
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t("debate_hot");
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = {
      key: "hot",
      label: t14,
      icon: FireIconSolid
    };
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t13 || $[36] !== t15) {
    t16 = [t13, t15];
    $[35] = t13;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = next => {
      if (next === "recent" || next === "hot") {
        setSort(next);
      }
    };
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("debate_sort");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== sort || $[42] !== t16 || $[43] !== t18) {
    t19 = <div className="mb-3"><SegmentedControl items={t16} active={sort} onChange={t17} layoutId="market-debate-sort" variant="tab" asTabs={true} ariaLabel={t18} /></div>;
    $[41] = sort;
    $[42] = t16;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const t20 = `${marketPubkey}:${sort}`;
  const t21 = hasNextPage === true;
  let t22;
  if ($[45] !== fetchNextPage || $[46] !== isFetchingNextPage || $[47] !== isPending || $[48] !== pageCount || $[49] !== t21) {
    t22 = {
      isLoading: isPending,
      hasNextPage: t21,
      isFetchingNextPage,
      fetchNextPage,
      pageCount
    };
    $[45] = fetchNextPage;
    $[46] = isFetchingNextPage;
    $[47] = isPending;
    $[48] = pageCount;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== openTradeForm) {
    t23 = openTradeForm !== undefined ? {
      onOpenTradeForm: openTradeForm
    } : {};
    $[51] = openTradeForm;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t) {
    t24 = t("theses_empty_title");
    $[53] = t;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t) {
    t25 = t("theses_empty_market");
    $[55] = t;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== isAuthenticated || $[58] !== replyWriteAccess || $[59] !== t20 || $[60] !== t22 || $[61] !== t23 || $[62] !== t24 || $[63] !== t25 || $[64] !== theses || $[65] !== userId) {
    t26 = <ThesisFeed key={t20} theses={theses} pagination={t22} isAuthenticated={isAuthenticated} userId={userId} replyWriteAccess={replyWriteAccess} {...t23} emptyTitle={t24} emptyDescription={t25} scrollMode="page-below-lg" />;
    $[57] = isAuthenticated;
    $[58] = replyWriteAccess;
    $[59] = t20;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
    $[64] = theses;
    $[65] = userId;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== marketPubkey || $[68] !== tradesFrom) {
    t27 = <MarketThesisPromptBar marketPubkey={marketPubkey} tradesFrom={tradesFrom} />;
    $[67] = marketPubkey;
    $[68] = tradesFrom;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t19 || $[71] !== t26 || $[72] !== t27) {
    t28 = <>{t19}{t26}{t27}</>;
    $[70] = t19;
    $[71] = t26;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}
function _temp(item) {
  return {
    signature: item.signature,
    marketPubkey: item.marketPubkey,
    trader: {
      ...item.trader,
      connectedProviders: []
    },
    outcomeIndex: item.outcomeIndex,
    outcomeLabel: item.outcomeLabel,
    outcomeImageUrl: item.outcomeImageUrl,
    outcomeImageNsfw: item.outcomeImageNsfw,
    content: item.content,
    createdAt: item.createdAt,
    rating: item.rating,
    viewerReaction: item.viewerReaction,
    replyCount: item.replyCount,
    tradeDirection: item.tradeDirection,
    tradeAmount: item.tradeAmount,
    marketTitle: item.marketTitle ?? "",
    marketType: item.marketType,
    partnerCreatorId: item.partnerCreatorId,
    associatedCreator: item.associatedCreator,
    marketCategory: item.marketCategory ?? "",
    marketStatus: item.marketStatus ?? 0
  };
}